import { getDirectApiBaseUrl, getApiHeaders } from 'mobile_web/modules/platform'
import { getDefaultAppInfo } from 'mobile_web/modules/settings/functions'
import { parseWebVTT, Cue } from './webvtt'

// Cache parsed cues per mediaItem+language for the session. Only definitive
// outcomes are cached — a 200 (cues, possibly []) or a 404 (no timecoded
// transcription) — so a track without cues isn't refetched on every reopen.
// Transient failures (other non-200 statuses, network/parse errors) are not
// cached, so a later call retries.
const cache = new Map<string, Cue[]>()

// Cache the raw VTT text per mediaItem+language for the session. `null` records
// a definitive 404 (no transcription); a missing key means "not fetched yet", so
// transient failures retry. Shared by fetchTimecodedCues and fetchTimecodedVtt
// so a media item's track is only fetched over the network once per language.
const vttCache = new Map<string, string | null>()

/** Test-only: reset the in-memory caches. */
export const __clearTranscriptionCache = (): void => {
	cache.clear()
	vttCache.clear()
}

// Fetches the raw WebVTT once and caches definitive outcomes: the text on 200,
// `null` on a definitive 404 (cached so it isn't refetched on reopen), or
// `undefined` on a transient failure (NOT cached, so the next call retries).
const fetchVttText = async (mediaItemId: number, language: string): Promise<string | null | undefined> => {
	const key = `${mediaItemId}:${language}`
	if (vttCache.has(key)) return vttCache.get(key)

	try {
		const { projectId, appApiKey } = getDefaultAppInfo()
		const url =
			`${getDirectApiBaseUrl()}/api/v3/media_items/${mediaItemId}` +
			`/timecoded_transcription?format=webvtt&language=${encodeURIComponent(language)}`
		const response = await fetch(url, {
			headers: {
				...getApiHeaders(),
				Authorization: 'Basic ' + btoa(`${projectId}:${appApiKey}`),
			},
		})
		if (response.status === 200) {
			const text = await response.text()
			vttCache.set(key, text)
			return text
		}
		if (response.status === 404) {
			vttCache.set(key, null)
			return null
		}
		return undefined // transient (5xx, etc.) — don't cache, retry later
	} catch {
		return undefined // network failure — don't cache, retry later
	}
}

export const fetchTimecodedCues = async (mediaItemId: number, language: string): Promise<Cue[]> => {
	const key = `${mediaItemId}:${language}`
	const cached = cache.get(key)
	if (cached) return cached

	const text = await fetchVttText(mediaItemId, language)
	if (text == null) {
		// null = definitive 404 → cache [] so we don't refetch; undefined =
		// transient/parse failure → leave uncached so a later call retries.
		if (text === null) cache.set(key, [])
		return []
	}
	const cues = parseWebVTT(text)
	cache.set(key, cues)
	return cues
}

/** Fetches the raw WebVTT text for a timecoded media item. Returns undefined when there is none (404) or on transient failure. */
export const fetchTimecodedVtt = async (mediaItemId: number, language: string): Promise<string | undefined> => {
	const text = await fetchVttText(mediaItemId, language)
	return text ?? undefined
}
